import { groupCartContent } from '@/utilities/groupByProviderAndFulfillment';

import { CartContent } from '@/client/services/vendor/models';

export const getCartItemsQuantity = (items?: Array<{ quantity: number }> | null) => items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;

/** Optimistically apply a line-item quantity change to a cart snapshot. */
export const applyCartItemQuantity = (cart: CartContent, itemId: string, quantity: number): CartContent => {
    const items = cart.items.map((item) => {
        if (item.itemId !== itemId || item.quantity === quantity || item.quantity <= 0) {
            return item;
        }

        const ratio = quantity / item.quantity;

        return {
            ...item,
            quantity,
            itemTotal: Math.round(item.itemTotal * ratio),
            discountAmount: Math.round(item.discountAmount * ratio)
        };
    });

    const previousItemsSubtotal = cart.items.reduce((sum, item) => sum + item.itemTotal, 0);
    const nextItemsSubtotal = items.reduce((sum, item) => sum + item.itemTotal, 0);
    const itemsDelta = nextItemsSubtotal - previousItemsSubtotal;

    return {
        ...cart,
        items,
        itemsSubtotal: cart.itemsSubtotal + itemsDelta,
        discountedItemsSubtotal: cart.discountedItemsSubtotal + itemsDelta,
        finalTotal: Math.max(cart.finalTotal + itemsDelta, 0)
    };
};

export const describeCartPricingChanges = (previous: CartContent, next: CartContent) => {
    const changes: Array<{
        field: string;
        label: string;
        previous: number;
        next: number;
        groupId: string;
        groupLabel: string;
        productPrevious?: number;
        productNext?: number;
        catalogProductUuid?: string;
    }> = [];
    const compare = (field: string, label: string, before: number, after: number, groupId = 'summary', groupLabel = 'خلاصه سبد خرید') => {
        if (before !== after) changes.push({ field, label, previous: before, next: after, groupId, groupLabel });
    };
    for (const item of next.items) {
        const before = previous.items.find((entry) => entry.itemId === item.itemId);
        if (!before) continue;
        const fields = [
            ['unitPrice', 'قیمت واحد'],
            ['discountAmount', 'تخفیف'],
            ['itemTotal', 'جمع کالا'],
            ['fulfillmentFee', 'هزینه ارسال / نصب']
        ] as const;
        for (const [field, label] of fields) {
            compare(`${item.itemId}:${field}`, label, before[field], item[field], item.itemId, item.title);
        }
        const adjustmentNames = new Set([...(before.priceAdjustments ?? []), ...(item.priceAdjustments ?? [])].map((entry) => entry.name));
        for (const name of adjustmentNames) {
            const total = (adjustments: typeof item.priceAdjustments) =>
                (adjustments ?? []).filter((entry) => entry.name === name).reduce((sum, entry) => sum + entry.totalPrice, 0);
            compare(`${item.itemId}:adjustment:${name}`, name, total(before.priceAdjustments), total(item.priceAdjustments), item.itemId, item.title);
        }
    }
    for (const change of changes) {
        const before = previous.items.find((item) => item.itemId === change.groupId);
        const after = next.items.find((item) => item.itemId === change.groupId);
        if (before && after) {
            change.productPrevious = before.itemTotal;
            change.productNext = after.itemTotal;
            change.catalogProductUuid = after.catalogProductUuid;
        }
    }
    compare('couponDiscount', `تخفیف کوپن ${previous.couponCode || next.couponCode || ''}`, previous.couponDiscount ?? 0, next.couponDiscount ?? 0);
    compare('fulfillmentTotal', 'جمع هزینه ارسال / نصب', previous.fulfillmentTotal, next.fulfillmentTotal);
    compare('priceAdjustmentsTotal', 'جمع اجرت و هزینه‌های جانبی', previous.priceAdjustmentsTotal ?? 0, next.priceAdjustmentsTotal ?? 0);
    compare('totalDiscount', 'جمع تخفیف‌ها', previous.totalDiscount, next.totalDiscount);
    compare('finalTotal', 'قیمت نهایی', previous.finalTotal, next.finalTotal);
    return changes;
};

export const withGroupedCart = (cart: CartContent | null) => ({
    cart,
    groupedCart: cart ? groupCartContent(cart) : null
});
